const KEY_FIELDS = ['CW_ACCT_ID', 'REC_CUST', 'REC_SRC_ACCT', 'REC_SEC_ID', 'CU_SEC_ID'];

const keyPart = (value) => (value === undefined ? null : value);

// Fields that tell recon rows apart for the grid. Rows can share a comment key
// (differing only in SEC_ID, say), so an occurrence index makes the id unique.
const ROW_ID_FIELDS = ['REC_ASOFDT', ...KEY_FIELDS, 'SEC_ID', 'CW_ID', 'CW_SEC_ID'];

/** `rows` with a unique, null-safe `__rowId`, stable while the row order is. */
export const withRowIds = (rows) => {
  const seen = new Map();
  return rows.map((row) => {
    const base = JSON.stringify(ROW_ID_FIELDS.map((field) => keyPart(row[field])));
    const n = seen.get(base) || 0;
    seen.set(base, n + 1);
    return { ...row, __rowId: `${base}#${n}` };
  });
};

/** Exact, null-preserving identity for the backend's latest-comment partition. */
export const commentKey = (row) => JSON.stringify(KEY_FIELDS.map((field) => keyPart(row?.[field])));

const blankToNull = (value) => (typeof value === 'string' && value.trim() === '' ? null : value ?? null);

export class DuplicateCommentRowsError extends Error {
  constructor() {
    super('Some edited breaks appear more than once with different saved comments. Reload and edit them again.');
    this.name = 'DuplicateCommentRowsError';
  }
}

/**
 * Record one comment edit. The first edit of a row keeps the comment and the
 * COMMENT_DATE it was loaded with; returning to the loaded comment drops it.
 */
export const recordCommentEdit = (edits, row, oldValue, newValue) => {
  const next = new Map(edits);
  const key = commentKey(row);
  const existing = next.get(key);
  const original = existing ? existing.original : oldValue ?? null;
  if (blankToNull(newValue) === blankToNull(original)) {
    next.delete(key);
    return next;
  }
  next.set(key, {
    row: existing?.row || { ...row },
    original,
    token: existing ? existing.token : row.COMMENT_DATE ?? null,
    value: newValue ?? null,
  });
  return next;
};

/**
 * Reapply drafts over reloaded rows. A draft survives only while its row still
 * carries the token it was loaded with: when someone else saved meanwhile, the
 * row shows their comment and the draft is dropped (never re-sent with their
 * token, which would silently overwrite them).
 */
export const reapplyCommentEdits = (rows, edits) => {
  const nextEdits = new Map();
  let conflicts = 0;
  const nextRows = rows.map((row) => {
    const key = commentKey(row);
    const edit = edits.get(key);
    if (!edit) return row;
    if ((row.COMMENT_DATE ?? null) !== edit.token) {
      conflicts += 1;
      return row;
    }
    nextEdits.set(key, { ...edit, row: { ...row } });
    return { ...row, COMMENTS: edit.value };
  });
  // A draft whose row is no longer loaded cannot be seen or undone: drop it.
  const loaded = new Set(rows.map(commentKey));
  edits.forEach((_edit, key) => {
    if (!loaded.has(key)) conflicts += 1;
  });
  return { rows: nextRows, edits: nextEdits, conflicts };
};

/** Payload for edited rows only, carrying exactly the token loaded with each row. */
export const buildCommentWrites = (rows, edits) => {
  const tokens = new Map();
  rows.forEach((row) => {
    const key = commentKey(row);
    if (!edits.has(key)) return;
    const token = row.COMMENT_DATE ?? null;
    if (tokens.has(key) && tokens.get(key) !== token) throw new DuplicateCommentRowsError();
    tokens.set(key, token);
  });
  return [...edits.values()].map((edit) => ({
    ...Object.fromEntries(KEY_FIELDS.map((field) => [field, keyPart(edit.row[field])])),
    COMMENTS: blankToNull(edit.value),
    COMMENT_DATE: edit.token ?? null,
  }));
};

/** Drafts left after a save: drop sent ones the user has not changed since. */
export const removeSavedEdits = (edits, sent) => {
  const next = new Map(edits);
  sent.forEach((edit, key) => {
    if (next.get(key)?.value === edit.value) next.delete(key);
  });
  return next;
};
